import { useState, useEffect } from "react";
import {
  X, Shield, AlertTriangle, Phone, ExternalLink,
  ChevronRight, ShieldAlert, ShieldCheck, Info
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  critical: { color: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert, label: "Critical" },
  high: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: AlertTriangle, label: "High Risk" },
  medium: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: Info, label: "Warning" },
};

const CATEGORY_LABELS = {
  banking: "Banking Fraud",
  lottery: "Lottery Scam",
  utility: "Utility Scam",
  impersonation: "Impersonation",
  upi: "UPI Fraud",
  employment: "Job Scam",
  identity: "Identity Theft",
  unknown: "Other",
};

export default function ScamAlertsPanel({ sessionId, onClose, onReportScam }) {
  const [alerts, setAlerts] = useState([]);
  const [helplines, setHelplines] = useState({});
  const [portals, setPortals] = useState({});
  const [safetyTips, setSafetyTips] = useState([]);
  const [reportedScams, setReportedScams] = useState([]);
  const [activeTab, setActiveTab] = useState("alerts");
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportDesc, setReportDesc] = useState("");
  const [reportType, setReportType] = useState("unknown");
  const [reportPhone, setReportPhone] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [alertsRes, tipsRes] = await Promise.all([
        axios.get(`${API}/scam-alerts`),
        axios.get(`${API}/safety-tips`),
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setReportedScams(alertsRes.data.reported_scams || []);
      setHelplines(alertsRes.data.helplines || {});
      setPortals(alertsRes.data.portals || {});
      setSafetyTips(tipsRes.data.tips || []);
    } catch (e) {
      console.error("Failed to load scam data:", e);
    }
  };

  const submitReport = async () => {
    if (!reportDesc.trim()) return;
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("description", reportDesc);
      formData.append("scam_type", reportType);
      formData.append("phone_number", reportPhone);
      await axios.post(`${API}/scam/report`, formData);
      setShowReport(false);
      setReportDesc("");
      setReportPhone("");
      if (onReportScam) onReportScam();
      loadData();
    } catch (e) {
      console.error("Report failed:", e);
    }
  };

  const tabs = [
    { id: "alerts", label: "Scam Alerts", icon: ShieldAlert },
    { id: "tips", label: "Safety Tips", icon: ShieldCheck },
    { id: "helplines", label: "Helplines", icon: Phone },
  ];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="scam-alerts-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ backgroundColor: "#2D2766" }}>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-white" />
            <h2 className="text-white font-semibold text-lg">Safety & Scam Alerts</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="close-scam-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white shrink-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "text-[#2D2766] border-b-2 border-[#2D2766]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`scam-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Alerts Tab */}
          {activeTab === "alerts" && (
            <div className="p-4 space-y-3">
              {/* Report button */}
              <button
                onClick={() => setShowReport(!showReport)}
                className="w-full flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                data-testid="report-scam-btn"
              >
                <ShieldAlert className="w-6 h-6 text-red-600" />
                <div className="text-left flex-1">
                  <p className="font-medium text-red-800">Report a Scam</p>
                  <p className="text-xs text-red-600">Help protect others in our community</p>
                </div>
                <ChevronRight className="w-4 h-4 text-red-400" />
              </button>

              {/* Report form */}
              {showReport && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="scam-report-form">
                  <textarea
                    placeholder="Describe what happened..."
                    value={reportDesc}
                    onChange={(e) => setReportDesc(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-red-300 min-h-[80px] resize-none"
                    data-testid="scam-description-input"
                  />
                  <input
                    type="tel"
                    placeholder="Scammer's phone number (if known)"
                    value={reportPhone}
                    onChange={(e) => setReportPhone(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-red-300"
                    data-testid="scam-phone-input"
                  />
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base bg-white"
                    data-testid="scam-type-select"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <button
                    onClick={submitReport}
                    disabled={!reportDesc.trim()}
                    className="w-full bg-red-500 text-white py-2.5 rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                    data-testid="submit-scam-report-btn"
                  >
                    Submit Report
                  </button>
                </div>
              )}

              {/* Alerts list */}
              {alerts.map((alert) => {
                const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                const SevIcon = sev.icon;
                const isExpanded = expandedAlert === alert.id;
                return (
                  <div
                    key={alert.id}
                    className={`border rounded-xl overflow-hidden ${sev.color}`}
                    data-testid={`scam-alert-${alert.id}`}
                  >
                    <button
                      onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                      className="w-full flex items-start gap-3 p-3 text-left"
                    >
                      <SevIcon className="w-5 h-5 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{alert.title}</span>
                          <Badge className="text-[10px] bg-white/60 border-0">{sev.label}</Badge>
                        </div>
                        <p className="text-sm leading-relaxed">{alert.description}</p>
                      </div>
                      <ChevronRight className={`w-4 h-4 mt-1 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0 ml-8 border-t border-current/10">
                        <p className="text-sm font-medium mt-2 mb-1">What to do:</p>
                        <p className="text-sm">{alert.action}</p>
                        <p className="text-xs mt-2 opacity-70">Source: {alert.source}</p>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Community reports */}
              {reportedScams.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-900 mb-2 text-sm">Recent Community Reports</h3>
                  {reportedScams.slice(0, 5).map((report) => (
                    <div key={report.id} className="bg-gray-50 border rounded-lg p-3 mb-2 text-sm">
                      <p className="text-gray-800">{report.description}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge className="text-[10px] bg-gray-200 text-gray-600 border-0">
                          {CATEGORY_LABELS[report.scam_type] || "Other"}
                        </Badge>
                        {report.phone_number && (
                          <span className="text-xs text-gray-400">Phone: {report.phone_number}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Safety Tips Tab */}
          {activeTab === "tips" && (
            <div className="p-4 space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-green-800 font-medium">Daily Safety Tips</p>
                <p className="text-xs text-green-600 mt-0.5">Simple steps to protect yourself from fraud</p>
              </div>
              {safetyTips.map((tip, index) => (
                <div
                  key={tip.id}
                  className="flex items-start gap-3 bg-white border rounded-xl p-3"
                  data-testid={`safety-tip-${tip.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#EDE8F8] flex items-center justify-center text-sm font-bold text-[#2D2766] shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{tip.tip}</p>
                </div>
              ))}
            </div>
          )}

          {/* Helplines Tab */}
          {activeTab === "helplines" && (
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-sm text-blue-800 font-medium">Emergency Helplines</p>
                <p className="text-xs text-blue-600 mt-0.5">Save these numbers for immediate help</p>
              </div>

              {Object.entries(helplines).map(([key, number]) => {
                const labels = {
                  cyber_crime: { name: "Cyber Crime Helpline", desc: "Report online fraud" },
                  police: { name: "Police", desc: "Emergency assistance" },
                  women_helpline: { name: "Women Helpline", desc: "Women safety & support" },
                  senior_citizen_helpline: { name: "Elderline", desc: "Senior citizen assistance" },
                  trai_dnd: { name: "TRAI DND", desc: "Block spam calls/SMS" },
                };
                const info = labels[key] || { name: key, desc: "" };
                return (
                  <a
                    key={key}
                    href={`tel:${number}`}
                    className="flex items-center gap-3 bg-white border rounded-xl p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`helpline-${key}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#E8B830] flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-base">{info.name}</p>
                      <p className="text-sm text-gray-500">{info.desc}</p>
                    </div>
                    <span className="text-xl font-bold text-[#2D2766]">{number}</span>
                  </a>
                );
              })}

              <h3 className="font-medium text-gray-900 mt-6 mb-2">Online Portals</h3>
              {Object.entries(portals).map(([key, url]) => {
                const labels = {
                  cyber_crime: "National Cyber Crime Portal",
                  sanchar_saathi: "Sanchar Saathi (TRAI)",
                  chakshu: "Chakshu - Report Spam",
                };
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white border rounded-xl p-3 hover:bg-gray-50 transition-colors"
                    data-testid={`portal-${key}`}
                  >
                    <ExternalLink className="w-5 h-5 text-[#34B7F1]" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{labels[key] || key}</p>
                      <p className="text-xs text-gray-400">{url}</p>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
